"use client";

import { useEffect, useState } from "react";
import { Menu, Square, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (loading) {
    return <div className="flex flex-col gap-6 px-6 pt-8">
        <div className="h-6 w-32 bg-muted animate-pulse" />
        <div className="h-6 w-28 bg-muted animate-pulse" />
        <div className="h-6 w-24 bg-muted animate-pulse" />
        <div className="h-6 w-32 bg-muted animate-pulse" />
      </div>;
  }
  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };
  return <nav className="flex flex-col gap-1 px-6 py-4">
      {menuItems.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      const isExpanded = expandedId === item.id;
      return <div key={item.id} data-index={i} className="flex flex-col">
            {hasChildren ? <div onClick={() => toggleExpand(item.id)} className="group flex cursor-pointer items-center justify-between border-b border-border py-4 text-foreground">
                <span className="text-[13px] font-semibold uppercase tracking-[0.15em] font-default">
                  {item.label}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
              </div> : <Link to={item.href || "#"} className="group flex items-center justify-between border-b border-border py-4 text-foreground">
                <span className="text-[13px] font-semibold uppercase tracking-[0.15em] font-default">
                  {item.label}
                </span>
                <Square className="h-2 w-2 fill-primary text-primary opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>}

            {hasChildren && isExpanded && <div className="flex flex-col pl-4">
                {item.children?.map((child, childIdx) => <Link key={child.id} to={child.href || "#"} className="group flex items-center justify-between border-b border-border/50 py-3 text-foreground/80 hover:text-foreground">
                    <span className="text-[12px] font-medium uppercase tracking-[0.1em] font-default">
                      {child.label}
                    </span>
                    <Square className="h-1.5 w-1.5 fill-primary/50 text-primary/50 opacity-0 transition-opacity group-hover:opacity-100" />
                  </Link>)}
              </div>}
          </div>;
    })}
    </nav>;
}
export default function Topleftmenu() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="2820" id="topleftmenu" className={`sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-[400ms] ease-out ${scrolled ? "bg-background/70 backdrop-blur-lg border-b border-foreground/10" : "bg-background border-b border-foreground/15"}`}>
      <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-5 md:px-10">
        <div className="flex items-center gap-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" className="h-11 w-11 text-foreground hover:bg-foreground/10 hover:text-foreground">
                <Menu className="h-6 w-6" />
              </Button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="start" className="w-72 p-0 bg-background border-border shadow-xl mt-2">
              <div className="border-b border-foreground/10 px-6 py-4">
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-muted-foreground font-default">
                  <Square className="h-2 w-2 fill-primary text-primary" />
                  Navigation
                </div>
              </div>
              <MenuProvider menu_id="18">
                <MobileMenuConsumer />
              </MenuProvider>
              <div className="border-t border-foreground/10 px-6 py-6">
                <span className="block text-[9px] font-semibold uppercase tracking-[0.25em] text-muted-foreground font-default">
                  Licensed · Insured
                </span>
                <Button asChild className="mt-3 h-10 w-full bg-primary text-[11px] font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-md hover:bg-primary/90">
                  <Link to="/ContactPage">Get Quote</Link>
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          <Link to="/" className="flex items-center" aria-label="Peace River Pest Control home">
            <WvcLogo className="h-6 w-auto" />
          </Link>
        </div>

        <div className="flex flex-1 justify-center">
          <Button asChild className="px-4 py-2 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive has-[>svg]:px-3 h-11 bg-primary text-[14px] font-bold uppercase tracking-[0.15em] text-primary-foreground shadow-md ![padding:8px_24px_8px_24px] ![margin:0px_0px_0px_75px] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">
            <Link to="tel:941-888-0485">941-888-0485</Link>
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex flex-col items-end leading-none">
            <Button asChild className="mt-2 h-11 bg-primary px-6 text-[12px] font-bold uppercase tracking-[0.2em] text-primary-foreground shadow-lg hover:bg-primary/90">
              <Link to="/ContactPage">Get Quote</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}